import { useEffect, useRef, useState } from "react";

export const PILL_ROTATE_INTERVAL_MS = 20_000;
export const PILL_EXIT_MS = 200;
export const PILL_ENTER_MS = 350;

export type PillAnimState = "open" | "closed";

export function pickRandomMessage(
  messages: readonly string[],
  exclude?: string,
): string {
  if (messages.length === 0) return "";
  if (messages.length === 1) return messages[0] ?? "";

  const pool =
    exclude && messages.includes(exclude)
      ? messages.filter((m) => m !== exclude)
      : messages;

  return pool[Math.floor(Math.random() * pool.length)] ?? "";
}

interface UseRotatingMessageOptions {
  messages: readonly string[];
  intervalMs?: number;
}

export function useRotatingMessage({
  messages,
  intervalMs = PILL_ROTATE_INTERVAL_MS,
}: UseRotatingMessageOptions) {
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const [message, setMessage] = useState(() =>
    pickRandomMessage(messagesRef.current),
  );
  const [animState, setAnimState] = useState<PillAnimState>("open");

  const messageRef = useRef(message);
  messageRef.current = message;

  const pendingRef = useRef<string | null>(null);
  const isTransitioningRef = useRef(false);

  const commitPending = () => {
    const next = pendingRef.current;
    if (!next) return;

    pendingRef.current = null;
    isTransitioningRef.current = false;
    setMessage(next);
    setAnimState("open");
  };

  const commitPendingRef = useRef(commitPending);
  commitPendingRef.current = commitPending;

  useEffect(() => {
    if (intervalMs <= 0 || messagesRef.current.length <= 1) return;

    const id = window.setInterval(() => {
      if (isTransitioningRef.current) return;

      const next = pickRandomMessage(messagesRef.current, messageRef.current);
      if (!next || next === messageRef.current) return;

      isTransitioningRef.current = true;
      pendingRef.current = next;
      setAnimState("closed");
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [intervalMs]);

  useEffect(() => {
    if (animState !== "closed") return;

    const fallback = window.setTimeout(() => {
      commitPendingRef.current();
    }, PILL_EXIT_MS + 50);

    return () => window.clearTimeout(fallback);
  }, [animState]);

  const onExitAnimationEnd = (animationName: string) => {
    if (animState !== "closed") return;
    if (!animationName.includes("fade-out")) return;
    commitPending();
  };

  return { message, animState, onExitAnimationEnd };
}
